import { Grid, Paper, Box, TextField } from "@mui/material";
import { Button } from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import * as React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UserInfo from "../../../../ui/UserInfo";
import { CustomTextField } from "../../../../ui/CustomTextField";
import { createSampleShipment } from "../../../../../redux/actions/sampleShipment";
import dayjs from "dayjs";
import { store } from "../../../../../store";
import { showSuccessToast } from "../../../../../shared/ui/toast/Toast";
import { cdlOperator } from "../../../../../shared/AccessHelper";

const combineDateAndTime = (dateValue, timeValue) => {
  const date = dayjs(dateValue);

  if (!date.isValid()) return null;

  if (typeof timeValue === "string" && /^\d{1,2}:\d{2}/.test(timeValue)) {
    const [hours, minutes] = timeValue.split(":");
    return date
      .hour(Number(hours))
      .minute(Number(minutes))
      .second(0)
      .millisecond(0)
      .toISOString();
  }

  const time = dayjs.isDayjs(timeValue) ? timeValue : dayjs(timeValue);
  if (!time.isValid()) return date.toISOString();

  return date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0)
    .toISOString();
};

const sampleAnalysisLabel = (sample = {}) =>
  (sample.analyses || []).map((analysis) => analysis.name).join(", ") || "-";

const sampleReferenceLabel = (sample = {}) => sample.reference || "-";

const sampleKitReferenceLabel = (sample = {}) =>
  sample.production?.reference ||
  sample.Production?.reference ||
  sample.productionReference ||
  sample.kitReference ||
  "-";

const formatSampleType = (type) => {
  if (!type) return "";
  return String(type).charAt(0).toUpperCase() + String(type).slice(1);
};

const sampleTypeLabel = (sample = {}) => {
  const explicitType =
    sample.sampleTypeDefinition?.name ||
    sample.SampleTypeDefinition?.name ||
    sample.sampleType?.name ||
    sample.SampleType?.name ||
    sample.sampleTypeName ||
    sample.type ||
    (typeof sample.sampleType === "string" ? sample.sampleType : "") ||
    sample.primaryType;
  if (explicitType) return formatSampleType(explicitType);

  const analysisSampleTypes = (sample.analyses || [])
    .flatMap((analysis) => analysis.sampleTypes || [])
    .map((sampleType) => formatSampleType(sampleType.name))
    .filter(Boolean);

  return [...new Set(analysisSampleTypes)].join(", ") || "-";
};

const samplePatientLabel = (sample = {}) =>
  sample.patientCode ||
  sample.patientKey ||
  sample.patient?.code ||
  sample.patient?.number ||
  "-";

const sampleQuantityLabel = (sample = {}) =>
  sample.quantity ?? sample.totalSamples ?? "-";

export const ScreenSampleRequestFormConfirm = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const analyses = useSelector((state) => state.analysis.analyses);
  const visits = useSelector((state) => state.visit.visits);
  const user = useSelector((state) => state.user.user);
  const centerId = location.state?.centerId;
  const shipmentData = location.state?.shipmentData;
  const shipmentSamples = shipmentData?.samples || [];
  const handleEditSampleShipment = () => {
    navigate("/sample-shipments/new", {
      state: {
        ...location.state,
        initialShipment: shipmentData,
      },
    });
  };

  const handleConfirmSampleShipment = async () => {
    try {
      const centerNumber = shipmentData.centerNumber;

      // Prepare the sample shipment data
      const pickupAddress = {
        building: shipmentData.address.building,
        centerNumber: shipmentData.address.centerNumber,
        city: shipmentData.address.city,
        contactEmail: shipmentData.address.contactEmail,
        contactName: shipmentData.address.contactName,
        contactPhone: shipmentData.address.contactPhone,
        country: shipmentData.address.country,
        floor: shipmentData.address.floor,
        hospital: shipmentData.address.hospital,
        street: shipmentData.address.street,
        ward: shipmentData.address.ward,
        zipcode: shipmentData.address.zipcode,
      };
      const destinationAddress = cdlOperator(user)
        ? shipmentData.address.destination || null
        : null;
      const sampleShipmentPayload = {
        centerId: shipmentData.centerId || centerId,
        pickupAddress,
        destinationAddress,
        airwaybill: shipmentData.airwaybill || null,
        sampleIds: shipmentSamples.map((sample) => sample.id),
        pickupDatetimeStart: combineDateAndTime(
          shipmentData.expectedShippingDate,
          shipmentData.pickUpSampleStartTime,
        ),
        pickupDatetimeEnd: combineDateAndTime(
          shipmentData.expectedShippingDate,
          shipmentData.pickUpSampleEndTime,
        ),
        createdByUserId: shipmentData.user.id,
        status: "requested",
      };

      const response = await store.dispatch(
        createSampleShipment(sampleShipmentPayload),
      );

      showSuccessToast(
        t("sampleShipmentRequested", "Sample shipment requested"),
      );
      const detailReference = response.data.reference;

      navigate(`/sample-shipments/${encodeURIComponent(detailReference)}`, {
        state: {
          ...location.state,
          sampleShipmentId: response.data.id,
          shipment: {
            ...shipmentData,
            reference: detailReference,
            status: response.data.status || "requested",
          },
        },
      });
    } catch (error) {
      console.error("Error creating sample shipment:", error);
      alert("Failed to confirm the sample shipment. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <Grid item xs={12}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 2,
        }}>
        <Box sx={{ p: 2 }}>
          <TextField
            value={t("shippingAddress").toUpperCase()}
            InputProps={{
              readOnly: true,
            }}
            inputProps={{
              style: { textAlign: "center" },
            }}
            fullWidth
            style={{ marginBottom: "16px" }}
          />

          <Grid container spacing={2}>
            {Object.entries(shipmentData.address).map(([key, value]) => {
              if (["comment"].includes(key)) {
                return null;
              }

              return (
                <CustomTextField
                  key={key}
                  label={t(key)}
                  name={key}
                  defaultValue={value}
                  disabled
                />
              );
            })}
          </Grid>
        </Box>
        <Box sx={{ p: 2 }}>
          <TextField
            value={t("samplesToShip").toUpperCase()}
            InputProps={{
              readOnly: true,
            }}
            inputProps={{
              style: { textAlign: "center" },
            }}
            fullWidth
            sx={{ mt: 1, mb: 1 }}
          />
          {shipmentSamples.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Ref</TableCell>
                    <TableCell>Kit Ref</TableCell>
                    <TableCell>
                      {t("primaryBackupAbbreviation", "PRIM / BU")}
                    </TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell>Analyses</TableCell>
                    <TableCell>Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipmentSamples.map((sample) => (
                    <TableRow key={sample.id}>
                      <TableCell>{sampleReferenceLabel(sample)}</TableCell>
                      <TableCell>{sampleKitReferenceLabel(sample)}</TableCell>
                      <TableCell>{sampleTypeLabel(sample)}</TableCell>
                      <TableCell>{samplePatientLabel(sample)}</TableCell>
                      <TableCell>{sampleAnalysisLabel(sample)}</TableCell>
                      <TableCell>{sampleQuantityLabel(sample)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            shipmentData.patients &&
            shipmentData.patients.map((patient, patientIndex) => (
              <section
                key={patientIndex}
                style={{
                  marginBottom: "16px",
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                }}
                id="patient-section">
                <h3>Patient ID {patient.selectedPatient}</h3>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Analysis</TableCell>
                        <TableCell>PRIM / BU</TableCell>
                        <TableCell>Visits</TableCell>
                        <TableCell>Shipping Temperature</TableCell>
                        <TableCell>Total Number of Samples</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {patient.samples &&
                        patient.samples.map((sample, sampleIndex) => (
                          <TableRow key={sampleIndex}>
                            <TableCell>
                              {
                                analyses.find(
                                  (a) => a.id === sample.selectedAnalysis,
                                )?.name
                              }
                            </TableCell>
                            <TableCell>{sample.sampleType}</TableCell>
                            <TableCell>
                              {sample.selectedVisits
                                .map(
                                  (visitId) =>
                                    visits.find((visit) => visit.id === visitId)
                                      ?.name,
                                )
                                .filter((name) => name)
                                .join(", ")}
                            </TableCell>
                            <TableCell>
                              {analyses.find(
                                (a) => a.id === sample.selectedAnalysis,
                              )?.temperature || "N/A"}
                            </TableCell>
                            <TableCell>{sample.totalSamples}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </section>
            ))
          )}
        </Box>
        <TextField
          value={t("transportInformation").toUpperCase()}
          InputProps={{
            readOnly: true,
          }}
          inputProps={{
            style: { textAlign: "center" },
          }}
          fullWidth
          sx={{ mt: 2 }}
        />
        <Grid container spacing={2} sx={{ mb: 2, ml: 2 }}>
          <Grid item xs={12}>
            <p>
              <strong>Expected Shipping Date:</strong>{" "}
              {formatDate(shipmentData.expectedShippingDate)} between{" "}
              {shipmentData.pickUpSampleStartTime} and{" "}
              {shipmentData.pickUpSampleEndTime}
            </p>
          </Grid>
        </Grid>
        <UserInfo
          title={t("personRequestingShipment").toUpperCase()}
          user={user}
          date={new Date().toLocaleDateString()}
        />
        <Grid container item xs={12} justifyContent={"space-between"}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleEditSampleShipment}
            style={{ padding: "16px 88px" }}>
            {t("editSampleShipment")}
          </Button>

          <Button
            variant="contained"
            color="primary"
            style={{ padding: "16px 88px" }}
            onClick={handleConfirmSampleShipment}>
            {t("confirmSampleShipment")}
          </Button>
        </Grid>
      </Paper>
    </Grid>
  );
};
